/**
 * Single-page stop creator/editor — Mapbox map on the left, form panel on the right.
 * Panel design mirrors CircuitControlPanel (gradient header, numbered steps, bg-gray-50 footer).
 * Student assignment lives in a secondary modal, consistent with the Assignments page pattern.
 */
import { useEffect, useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  MapPin,
  Copy,
  Search,
  X,
  LocateFixed,
  Loader2,
  Users,
  Lock,
  AlertCircle,
  CheckCircle,
  Save,
} from 'lucide-react';
import { MOROCCO_CENTERS } from '../config/mapboxConfig';
import { mapboxMapHideLogoClass } from '../utils/mapboxRtl';
import { acquireSharedMap, onSharedMapReady, releaseSharedMap, registerMapCleanup } from '../utils/sharedMapInstance';
import { getMarkerIcon } from '../utils/markerIcons';
import useStopWizard, { formatManualCoord } from '../hooks/trStopWizard';

const WIZARD_MAP_FALLBACK_CENTER = MOROCCO_CENTERS.rabat;

function escapeHtml(text) {
  return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const StopWizardPage = () => {
  const {
    navigate,
    isEdit,
    isMapStepLocked,
    showStudentsModal,
    openStudentsModal,
    cancelStudentsModal,
    confirmStudentsModal,
    schoolGeo,
    schoolMeta,
    schoolGeoRef,
    schoolMetaRef,
    pinLat,
    pinLng,
    manualLat,
    setManualLat,
    manualLng,
    setManualLng,
    pinRef,
    handleMapClick,
    hasReturnPoint,
    toggleReturnPoint,
    returnPinLat,
    returnPinLng,
    returnPinRef,
    manualReturnLat,
    setManualReturnLat,
    manualReturnLng,
    setManualReturnLng,
    applyManualReturnCoords,
    activePinTarget,
    setActivePinTarget,
    isLocating,
    locationError,
    requestAdminLocation,
    stopName,
    setStopName,
    address,
    setAddress,
    postalCode,
    setPostalCode,
    city,
    setCity,
    district,
    setDistrict,
    pickupTime,
    setPickupTime,
    dropoffTime,
    setDropoffTime,
    students,
    studentsLoading,
    studentSearch,
    setStudentSearch,
    selectedStudentIds,
    filteredStudents,
    studentOptionLabel,
    addedIds,
    removedIds,
    toggleStudent,
    showRecap,
    setShowRecap,
    submitting,
    loadingStop,
    applyManualCoords,
    handleCopySelectedCoordinates,
    runSubmit,
    validateAll,
  } = useStopWizard();

  // Step completion indicators (mirrors CircuitControlPanel logic)
  const step1Complete = pinLat != null && pinLng != null;
  const step2Complete = Boolean(stopName.trim() && address.trim());
  const step3Complete = selectedStudentIds.length > 0;
  const isFormValid = step1Complete && step2Complete;

  const validationMessage = !step1Complete
    ? 'Cliquez sur la carte pour placer un repère.'
    : !stopName.trim()
      ? "Nom de l'arrêt requis."
      : 'Adresse complète requise.';

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const returnMarkerRef = useRef(null);
  const schoolMarkerRef = useRef(null);
  const schoolMarkerMapRef = useRef(null);

  const syncSchoolMarker = useCallback(
    (map) => {
      if (!map) return;
      const meta = schoolMetaRef.current;
      const geo = schoolGeoRef.current;
      const latN = geo != null ? Number(geo.lat) : NaN;
      const lngN = geo != null ? Number(geo.lng) : NaN;
      const hasCoords =
        geo != null &&
        !Number.isNaN(latN) &&
        !Number.isNaN(lngN) &&
        latN >= -90 &&
        latN <= 90 &&
        lngN >= -180 &&
        lngN <= 180;

      if (!hasCoords) {
        if (schoolMarkerRef.current) {
          try {
            schoolMarkerRef.current.remove();
          } catch (_) {
            /* ignore */
          }
          schoolMarkerRef.current = null;
        }
        schoolMarkerMapRef.current = null;
        return;
      }

      if (schoolMarkerRef.current && schoolMarkerMapRef.current !== map) {
        try {
          schoolMarkerRef.current.remove();
        } catch (_) {
          /* ignore */
        }
        schoolMarkerRef.current = null;
        schoolMarkerMapRef.current = null;
      }

      if (schoolMarkerRef.current) {
        schoolMarkerRef.current.setLngLat([lngN, latN]);
        return;
      }

      const el = document.createElement('div');
      el.className = 'custom-school-marker';
      el.style.width = '48px';
      el.style.height = '48px';
      el.style.cursor = 'pointer';
      el.style.backgroundImage = `url('${getMarkerIcon('school')}')`;
      el.style.backgroundSize = 'contain';
      el.style.backgroundRepeat = 'no-repeat';
      el.style.backgroundPosition = 'center';

      schoolMarkerRef.current = new mapboxgl.Marker({
        element: el,
        anchor: 'bottom',
      })
        .setLngLat([lngN, latN])
        .setPopup(
          new mapboxgl.Popup({ offset: 25 }).setHTML(
            `<div style="padding: 8px;">
            <h3 style="margin: 0 0 4px 0; font-weight: 600;">🏫 ${escapeHtml(meta.name || 'École')}</h3>
            <p style="margin: 0; font-size: 12px; color: #666;">École</p>
          </div>`,
          ),
        )
        .addTo(map);
      schoolMarkerMapRef.current = map;
    },
    [schoolGeoRef, schoolMetaRef],
  );

  // Map lifecycle — instance Mapbox partagée (reuseMap), pas de map load
  // supplémentaire à chaque ouverture du wizard. Voir utils/sharedMapInstance.js.
  useEffect(() => {
    if (loadingStop) return;
    const container = mapContainerRef.current;
    if (!container) return;

    const pin = pinRef.current;
    const sg = schoolGeoRef.current;
    const hasPin =
      pin.lat != null && pin.lng != null && !Number.isNaN(Number(pin.lat)) && !Number.isNaN(Number(pin.lng));
    const hasSchoolGeo = sg != null && !Number.isNaN(Number(sg.lat)) && !Number.isNaN(Number(sg.lng));
    const center = hasPin
      ? [pin.lng, pin.lat]
      : hasSchoolGeo
        ? [sg.lng, sg.lat]
        : [WIZARD_MAP_FALLBACK_CENTER.lng, WIZARD_MAP_FALLBACK_CENTER.lat];

    // container.parentElement = div relatif qui inclut aussi le bouton "Utiliser ma position"
    const owner = {};
    const map = acquireSharedMap(
      container,
      {
        center,
        zoom: hasPin ? 14 : 13,
        fullscreenContainer: container.parentElement,
      },
      owner,
    );
    mapRef.current = map;

    const onClick = (e) => {
      if (isMapStepLocked) return;
      handleMapClick(e.lngLat.lat, e.lngLat.lng);
    };
    map.on('click', onClick);
    registerMapCleanup(() => map.off('click', onClick));

    let cancelled = false;
    onSharedMapReady(() => {
      if (cancelled) return;
      map.resize();
      const p = pinRef.current;
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      if (p.lat != null && p.lng != null) {
        map.flyTo({
          center: [p.lng, p.lat],
          zoom: Math.max(map.getZoom(), 14),
        });
        markerRef.current = new mapboxgl.Marker({ color: '#2563eb' }).setLngLat([p.lng, p.lat]).addTo(map);
      }
      const rp = returnPinRef.current;
      if (returnMarkerRef.current) {
        returnMarkerRef.current.remove();
        returnMarkerRef.current = null;
      }
      if (rp.lat != null && rp.lng != null) {
        returnMarkerRef.current = new mapboxgl.Marker({ color: '#f59e0b' }).setLngLat([rp.lng, rp.lat]).addTo(map);
      }
      syncSchoolMarker(map);
      map.once('idle', () => {
        if (!cancelled) syncSchoolMarker(map);
      });
    });

    return () => {
      cancelled = true;
      if (schoolMarkerRef.current) {
        try {
          schoolMarkerRef.current.remove();
        } catch (_) {
          /* ignore */
        }
        schoolMarkerRef.current = null;
      }
      schoolMarkerMapRef.current = null;
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      if (returnMarkerRef.current) {
        returnMarkerRef.current.remove();
        returnMarkerRef.current = null;
      }
      releaseSharedMap(owner);
      mapRef.current = null;
    };
  }, [loadingStop, handleMapClick, syncSchoolMarker, isMapStepLocked, pinRef, returnPinRef, schoolGeoRef]);

  // Re-sync school marker when school data loads after map init
  useEffect(() => {
    if (loadingStop) return;
    const map = mapRef.current;
    if (!map) return;
    const run = () => {
      syncSchoolMarker(map);
      requestAnimationFrame(() => syncSchoolMarker(map));
    };
    if (map.loaded()) {
      run();
    } else {
      map.once('load', run);
    }
    return () => {
      map.off('load', run);
    };
  }, [loadingStop, schoolMeta.name, schoolGeo?.lat, schoolGeo?.lng, syncSchoolMarker]);

  // Move map center to school when no pin yet
  useEffect(() => {
    if (loadingStop || !mapRef.current || pinLat != null || pinLng != null) return;
    const c = schoolGeo ?? WIZARD_MAP_FALLBACK_CENTER;
    mapRef.current.flyTo({ center: [c.lng, c.lat] });
  }, [loadingStop, schoolGeo?.lat, schoolGeo?.lng, pinLat, pinLng]);

  // Keep marker in sync with pin state; flyTo so "Centrer" pans the map
  useEffect(() => {
    if (loadingStop || pinLat == null || pinLng == null || !mapRef.current) return;
    const map = mapRef.current;
    if (markerRef.current) {
      markerRef.current.setLngLat([pinLng, pinLat]);
    } else {
      markerRef.current = new mapboxgl.Marker({ color: '#2563eb' }).setLngLat([pinLng, pinLat]).addTo(map);
    }
    map.flyTo({ center: [pinLng, pinLat], zoom: Math.max(map.getZoom(), 14) });
  }, [loadingStop, pinLat, pinLng]);

  // Keep return-point marker in sync — même logique que le point principal,
  // couleur distincte (orange) pour le différencier sur la carte. Supprime le
  // marqueur si le point retour est désactivé/effacé (returnPinLat redevient null).
  useEffect(() => {
    if (loadingStop || !mapRef.current) return;
    const map = mapRef.current;
    if (returnPinLat == null || returnPinLng == null) {
      if (returnMarkerRef.current) {
        returnMarkerRef.current.remove();
        returnMarkerRef.current = null;
      }
      return;
    }
    if (returnMarkerRef.current) {
      returnMarkerRef.current.setLngLat([returnPinLng, returnPinLat]);
    } else {
      returnMarkerRef.current = new mapboxgl.Marker({ color: '#f59e0b' })
        .setLngLat([returnPinLng, returnPinLat])
        .addTo(map);
    }
    map.flyTo({ center: [returnPinLng, returnPinLat], zoom: Math.max(map.getZoom(), 14) });
  }, [loadingStop, returnPinLat, returnPinLng]);

  if (loadingStop) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 min-h-full">
      {/* Page header — mirrors frontAssignments header */}
      <div className="px-4 py-4 shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{isEdit ? 'Modifier un arrêt' : 'Ajouter un arrêt'}</h1>
            <p className="text-gray-600">
              {isEdit
                ? "Modifiez les informations et la localisation de l'arrêt"
                : 'Définissez la localisation et les informations du nouvel arrêt'}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => navigate('/admin/stops')}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              aria-label="Retour à la liste des arrêts"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Retour
            </button>
          </div>
        </div>
      </div>

      {/* Split layout — column on mobile/tablet, side-by-side on xl+ */}
      <div className="flex flex-col xl:flex-row flex-1 xl:min-h-[600px]">
        {/* Map — fixed height on mobile/tablet, flex-1 on desktop */}
        <div className="h-[380px] shrink-0 xl:h-auto xl:flex-1 relative">
          <div
            ref={mapContainerRef}
            className={`absolute inset-0 ${mapboxMapHideLogoClass} ${isMapStepLocked ? 'pointer-events-none' : ''}`}
          />

          {/* Locked banner */}
          {isMapStepLocked && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
              <div className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/95 px-3 py-2 text-sm font-medium text-amber-800 shadow-md backdrop-blur-sm">
                <Lock className="h-4 w-4 shrink-0" aria-hidden />
                Localisation verrouillée — cet arrêt est assigné à un circuit
              </div>
            </div>
          )}

          {/* Geolocation button */}
          {!isMapStepLocked && (
            <div className="absolute left-3 top-3 z-10 flex flex-col items-start gap-1.5">
              <button
                type="button"
                onClick={requestAdminLocation}
                disabled={isLocating}
                aria-label={
                  isLocating
                    ? 'Localisation en cours'
                    : activePinTarget === 'return'
                      ? 'Utiliser ma position pour le point retour'
                      : 'Utiliser ma position'
                }
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium shadow-md backdrop-blur-sm transition-colors ${
                  isLocating
                    ? 'cursor-not-allowed border-gray-200 bg-white/80 text-gray-400'
                    : activePinTarget === 'return'
                      ? 'border-amber-200 bg-white/90 text-amber-700 hover:bg-white'
                      : 'border-blue-200 bg-white/90 text-blue-700 hover:bg-white'
                }`}
              >
                {isLocating ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <LocateFixed className="h-4 w-4" aria-hidden />
                )}
                {isLocating
                  ? 'Localisation en cours…'
                  : activePinTarget === 'return'
                    ? 'Utiliser ma position (retour)'
                    : 'Utiliser ma position'}
              </button>
              {locationError && (
                <p
                  className="rounded-md border border-red-200 bg-white/95 px-2 py-1 text-xs text-red-600 shadow-sm"
                  role="alert"
                >
                  {locationError}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Right panel — full width on mobile/tablet, fixed 384px on desktop */}
        <div className="w-full xl:w-96 bg-white border-t xl:border-t-0 xl:border-l border-gray-200 flex flex-col xl:shrink-0">
          {/* Panel header — gradient identical to CircuitControlPanel */}
          <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center space-x-2 mb-2">
              <MapPin className="text-blue-600" size={20} />
              <h2 className="font-semibold text-gray-900">{isEdit ? "Modifier l'arrêt" : 'Nouvel Arrêt'}</h2>
            </div>
            <p className="text-sm text-gray-600">
              {isEdit ? "Modifiez les informations de l'arrêt" : 'Configurez votre arrêt étape par étape'}
            </p>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* ── Étape 1 : Coordonnées GPS ── */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    step1Complete ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  1
                </div>
                <h3 className="font-medium text-gray-900">Coordonnées GPS</h3>
              </div>

              <div className="ml-8 space-y-3">
                {/* Selected point card */}
                {step1Complete ? (
                  <div
                    className="flex items-center gap-2 rounded-xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50/80 px-3 py-2 shadow-sm"
                    role="status"
                    aria-label={`Point sélectionné : ${formatManualCoord(pinLat)}, ${formatManualCoord(pinLng)}`}
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
                      <MapPin className="h-4 w-4" aria-hidden />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-blue-800">Point sélectionné</p>
                      <p className="font-mono text-sm font-semibold text-gray-900 tabular-nums truncate">
                        {formatManualCoord(pinLat)}&nbsp;·&nbsp;
                        {formatManualCoord(pinLng)}
                      </p>
                      <p className="text-[10px] text-gray-500">WGS 84</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleCopySelectedCoordinates}
                      title="Copier les coordonnées"
                      className="inline-flex items-center justify-center p-1.5 text-blue-700 bg-white/70 border border-blue-200 rounded-md hover:bg-white"
                    >
                      <Copy className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    {isMapStepLocked
                      ? 'Coordonnées verrouillées — arrêt assigné à un circuit'
                      : 'Cliquez sur la carte pour placer un repère'}
                  </p>
                )}

                {!isMapStepLocked && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                        <input
                          type="text"
                          value={manualLat}
                          onChange={(e) => setManualLat(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="33.9716"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                        <input
                          type="text"
                          value={manualLng}
                          onChange={(e) => setManualLng(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="-6.8498"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={applyManualCoords}
                      className="w-full rounded-lg px-3 py-2 text-sm font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                    >
                      Centrer sur ces coordonnées
                    </button>

                    {/* Point retour — optionnel, boulevard/sens unique où le bus dépose
                        de l'autre côté de la rue le soir (voir CLAUDE.md). */}
                    <div className="pt-3 border-t border-gray-100">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={hasReturnPoint}
                          onChange={(e) => toggleReturnPoint(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Point de dépose différent au retour</span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1 ml-6">
                        Grand boulevard / sens unique : le bus ne peut pas déposer au même point que le ramassage du
                        matin.
                      </p>

                      {hasReturnPoint && (
                        <div className="mt-2 space-y-2">
                          {/* Sélecteur de cible — détermine quel point le clic carte / la
                              géolocalisation modifie. Les deux marqueurs restent visibles sur
                              la carte (bleu = matin, orange = retour) quel que soit le choix. */}
                          <div
                            className="flex rounded-lg border border-gray-300 p-0.5"
                            role="radiogroup"
                            aria-label="Point modifié par la carte"
                          >
                            <button
                              type="button"
                              role="radio"
                              aria-checked={activePinTarget === 'main'}
                              onClick={() => setActivePinTarget('main')}
                              className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                                activePinTarget === 'main'
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              ● Point matin
                            </button>
                            <button
                              type="button"
                              role="radio"
                              aria-checked={activePinTarget === 'return'}
                              onClick={() => setActivePinTarget('return')}
                              className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                                activePinTarget === 'return'
                                  ? 'bg-amber-500 text-white'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              ● Point retour
                            </button>
                          </div>
                          <p className="text-xs text-gray-500">
                            {activePinTarget === 'return'
                              ? 'Cliquez sur la carte (ou utilisez votre position) pour placer le point retour.'
                              : 'Cliquez sur la carte (ou utilisez votre position) pour replacer le point matin.'}
                          </p>

                          {returnPinLat != null && returnPinLng != null && (
                            <div
                              className="flex items-center gap-2 rounded-xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50/80 px-3 py-2 shadow-sm"
                              role="status"
                              aria-label={`Point retour sélectionné : ${formatManualCoord(returnPinLat)}, ${formatManualCoord(returnPinLng)}`}
                            >
                              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500 text-white">
                                <MapPin className="h-4 w-4" aria-hidden />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800">
                                  Point retour sélectionné
                                </p>
                                <p className="font-mono text-sm font-semibold text-gray-900 tabular-nums truncate">
                                  {formatManualCoord(returnPinLat)}&nbsp;·&nbsp;
                                  {formatManualCoord(returnPinLng)}
                                </p>
                              </div>
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Latitude (retour)</label>
                              <input
                                type="text"
                                value={manualReturnLat}
                                onChange={(e) => setManualReturnLat(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                placeholder="33.9716"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Longitude (retour)</label>
                              <input
                                type="text"
                                value={manualReturnLng}
                                onChange={(e) => setManualReturnLng(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                placeholder="-6.8498"
                              />
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={applyManualReturnCoords}
                            className="w-full rounded-lg px-3 py-2 text-sm font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                          >
                            Valider le point retour
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ── Étape 2 : Informations générales ── */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    step2Complete ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  2
                </div>
                <h3 className="font-medium text-gray-900">Informations générales</h3>
              </div>

              <div className="ml-8 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l&apos;arrêt *</label>
                  <input
                    type="text"
                    value={stopName}
                    onChange={(e) => setStopName(e.target.value)}
                    placeholder="Arrêt Principale"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adresse complète *</label>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="123 Rue de l'Arrêt"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CP</label>
                    <input
                      type="text"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quartier</label>
                    <input
                      type="text"
                      value={district}
                      onChange={(e) => setDistrict(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ramassage <span className="text-gray-500 font-normal text-xs">(opt.)</span>
                    </label>
                    <input
                      type="time"
                      value={pickupTime}
                      onChange={(e) => setPickupTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dépose <span className="text-gray-500 font-normal text-xs">(opt.)</span>
                    </label>
                    <input
                      type="time"
                      value={dropoffTime}
                      onChange={(e) => setDropoffTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Étape 3 : Élèves assignés ── */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      step3Complete ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                    }`}
                  >
                    3
                  </div>
                  <h3 className="font-medium text-gray-900">Élèves assignés</h3>
                </div>
                <button
                  type="button"
                  onClick={openStudentsModal}
                  className="rounded-lg px-3 py-1.5 text-sm font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                >
                  {step3Complete ? `Modifier (${selectedStudentIds.length})` : 'Assigner des élèves'}
                </button>
              </div>

              <div className="ml-8 space-y-2">
                {!step3Complete ? (
                  <p className="text-sm text-gray-500 italic">Aucun élève assigné</p>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    <AnimatePresence>
                      {selectedStudentIds.map((id) => {
                        const s = students.find((x) => x.id === id);
                        if (!s) return null;
                        const isNew = !isEdit || addedIds.includes(id);
                        return (
                          <motion.div
                            key={id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            className="flex items-center justify-between py-1"
                          >
                            <div className="flex items-center space-x-2">
                              <div
                                className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  isNew ? 'bg-green-100' : 'bg-blue-100'
                                }`}
                              >
                                <Users size={10} className={isNew ? 'text-green-600' : 'text-blue-600'} />
                              </div>
                              <span className="text-sm text-gray-700 truncate">
                                {s.firstName} {s.lastName}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => toggleStudent(id)}
                              className="text-gray-400 hover:text-gray-600 transition-colors ml-2 shrink-0"
                              aria-label={`Retirer ${s.firstName} ${s.lastName}`}
                            >
                              <X size={14} />
                            </button>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  </div>
                )}

                {isEdit && removedIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {removedIds.map((id) => {
                      const s = students.find((x) => x.id === id);
                      return s ? (
                        <span
                          key={id}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"
                        >
                          {s.firstName} {s.lastName}
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Panel footer — bg-gray-50 + validity indicator, identical to CircuitControlPanel */}
          <div className="p-4 border-t bg-gray-50">
            <div className="mb-3">
              {isFormValid ? (
                <div className="flex items-center space-x-2 text-green-600 text-sm">
                  <CheckCircle size={16} />
                  <span>{isEdit ? 'Prêt à enregistrer' : "Prêt à créer l'arrêt"}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2 text-orange-600 text-sm">
                  <AlertCircle size={16} />
                  <span>{validationMessage}</span>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                if (validateAll()) setShowRecap(true);
              }}
              disabled={!isFormValid}
              className={`w-full flex items-center justify-center space-x-2 py-3 rounded-lg font-medium transition-all ${
                isFormValid
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Save size={16} />
              <span>{isEdit ? 'Enregistrer les modifications' : "Créer l'arrêt"}</span>
            </button>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════ Modal élèves ═══════════════════════════ */}
      <AnimatePresence>
        {showStudentsModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={cancelStudentsModal}
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col"
                style={{ maxHeight: '80vh' }}
              >
                <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Users className="text-blue-600" size={20} />
                      <h2 className="text-lg font-semibold text-gray-900">Assigner les élèves</h2>
                    </div>
                    <button
                      type="button"
                      onClick={cancelStudentsModal}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label="Fermer"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    {selectedStudentIds.length} élève
                    {selectedStudentIds.length !== 1 ? 's' : ''} sélectionné
                    {selectedStudentIds.length !== 1 ? 's' : ''}
                  </p>
                </div>

                <div className="px-6 pt-4 pb-2 shrink-0">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
                    <input
                      type="text"
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      placeholder="Rechercher par nom…"
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      autoFocus
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-2">
                  {studentsLoading ? (
                    <p className="text-sm text-gray-500 py-6 text-center">Chargement des élèves…</p>
                  ) : filteredStudents.length === 0 ? (
                    <p className="text-sm text-gray-400 py-6 text-center italic">Aucun élève trouvé</p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {filteredStudents.map((s) => (
                        <li key={s.id}>
                          <label className="flex items-center gap-3 py-2.5 px-1 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                            <input
                              type="checkbox"
                              checked={selectedStudentIds.includes(s.id)}
                              onChange={() => toggleStudent(s.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-900">{studentOptionLabel(s)}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {selectedStudentIds.length > 0 && (
                  <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 shrink-0">
                    <div className="flex flex-wrap gap-1.5">
                      {selectedStudentIds.slice(0, 8).map((id) => {
                        const s = students.find((x) => x.id === id);
                        if (!s) return null;
                        const isNew = !isEdit || addedIds.includes(id);
                        return (
                          <span
                            key={id}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isNew ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}
                          >
                            {s.firstName} {s.lastName}
                            <button
                              type="button"
                              onClick={() => toggleStudent(id)}
                              className="p-0.5 rounded hover:bg-black/10"
                              aria-label={`Retirer ${s.firstName} ${s.lastName}`}
                            >
                              <X className="w-2.5 h-2.5" aria-hidden />
                            </button>
                          </span>
                        );
                      })}
                      {selectedStudentIds.length > 8 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                          +{selectedStudentIds.length - 8}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="p-6 border-t bg-gray-50 flex items-center justify-end space-x-3 shrink-0">
                  <button
                    type="button"
                    onClick={cancelStudentsModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={confirmStudentsModal}
                    className="px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl"
                  >
                    <CheckCircle size={16} />
                    <span>Confirmer la sélection</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════ Modal récapitulatif ═══════════════════════════ */}
      <AnimatePresence>
        {showRecap && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowRecap(false)}
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="bg-white rounded-lg shadow-xl max-w-lg w-full flex flex-col"
                style={{ maxHeight: '90vh' }}
              >
                <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <MapPin className="text-blue-600" size={24} />
                      <h2 className="text-xl font-semibold text-gray-900">
                        {isEdit ? 'Confirmation de modification' : 'Confirmation de création'}
                      </h2>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowRecap(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Vérifiez les informations avant de {isEdit ? 'modifier' : 'créer'} l&apos;arrêt
                  </p>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <h3 className="font-semibold text-gray-900 flex items-center space-x-2 mb-3">
                      <MapPin size={16} />
                      <span>Informations arrêt</span>
                    </h3>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 text-sm">Nom :</span>
                      <span className="font-medium text-gray-900 text-sm">{stopName}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 text-sm">Adresse :</span>
                      <span className="font-medium text-gray-900 text-sm text-right max-w-[60%]">{address}</span>
                    </div>
                    {pinLat != null && pinLng != null && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">Coordonnées :</span>
                        <span className="font-mono text-xs text-gray-500">
                          {formatManualCoord(pinLat)}, {formatManualCoord(pinLng)}
                        </span>
                      </div>
                    )}
                    {hasReturnPoint && returnPinLat != null && returnPinLng != null && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">Point retour :</span>
                        <span className="font-mono text-xs text-gray-500">
                          {formatManualCoord(returnPinLat)}, {formatManualCoord(returnPinLng)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <h3 className="font-semibold text-gray-900 flex items-center space-x-2 mb-3">
                      <Users size={16} />
                      <span>Élèves assignés</span>
                    </h3>
                    {selectedStudentIds.length === 0 ? (
                      <p className="text-sm text-gray-500">Aucun élève sélectionné</p>
                    ) : (
                      <ul className="text-sm space-y-1 max-h-40 overflow-auto">
                        {selectedStudentIds.map((id) => {
                          const s = students.find((x) => x.id === id);
                          return s ? (
                            <li
                              key={id}
                              className={!isEdit || addedIds.includes(id) ? 'text-green-700' : 'text-gray-800'}
                            >
                              {s.firstName} {s.lastName}
                            </li>
                          ) : null;
                        })}
                      </ul>
                    )}
                    {isEdit && removedIds.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-red-600 font-medium">Désassignés</p>
                        <ul className="text-sm text-red-700 mt-1">
                          {removedIds.map((id) => {
                            const s = students.find((x) => x.id === id);
                            return s ? (
                              <li key={id}>
                                {s.firstName} {s.lastName}
                              </li>
                            ) : null;
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-6 border-t bg-gray-50 flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowRecap(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={runSubmit}
                    className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 ${
                      submitting
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                    }`}
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>En cours…</span>
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        <span>{isEdit ? 'Enregistrer' : "Créer l'arrêt"}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StopWizardPage;
